import { CriteriaBreakdown, StrategyStability } from "types/assessmentTypes";
import {
  clasterDatas,
  finalStrategy,
  IAWeights,
  StrategicBoundaryEvaluationSBE,
  StrategicDecisionEngineSDE,
  StrategicRangeClassificationSRC,
} from "variables/strategyDatas";
import { getDisplayPercent } from "./reportHelper";

// ============================================================================
// TYPES
// ============================================================================

export interface StrategicLevel {
  code: string;
  title: string;
  min: number;
  max: number;
  transition?: boolean;
}

export interface RuleResult {
  RULE: string;
  IA: string;
  CP: string;
  ER: string;
  description: string;
  Confidence: string;
  StrategicDirection: string;
}

export interface FinalStrategyResult {
  order: number;
  T: string[];
  DecisionScope: number;
  StrategicStability: StrategyStability;
}

export interface StrategyResult {
  // Step 1: Raw scores
  scores: {
    ER: number;
    CP: number;
    IA: number;
  };

  // Step 2: Strategic levels
  levels: {
    ER: StrategicLevel;
    CP: StrategicLevel;
    IA: StrategicLevel;
  };

  // Step 3: All evaluated rules and combinations
  allRules: RuleResult[];
  allCombinations: Array<{ ER: string; CP: string; IA: string }>;

  // Step 4: Primary and secondary strategies
  primaryStrategy: string;
  secondaryStrategy?: string;
  isTransitional: boolean;
  transitions: string[];
  strategyCounts: Record<string, number>;

  // Step 5: Final strategy
  finalStrategy: FinalStrategyResult | null;
  boundaryEvaluation: {
    transitionCount: number;
    ruleCount: number;
    StrategicStability: StrategyStability;
    DecisionStatus: string;
  };
}

// ============================================================================
// CONSTANTS
// ============================================================================

const STRATEGY_PRIORITY: Record<string, number> = {
  Growth: 1,
  Maintain: 2,
  AnalyzeReview: 3,
  HarvestExit: 4,
};

// ============================================================================
// STEP 1: Calculate Cluster Scores
// ============================================================================

/**
 * Calculates the score for a specific cluster based on criteria breakdown
 */
export const calculateClusterScore = (
  criteriaBreakdown: CriteriaBreakdown[],
  clusterName: string,
  industryWeights: { [key: number]: { [key: string]: number } },
  industryKey: string
): number => {
  const cluster = clasterDatas.find((c) => c.clasterName === clusterName);
  if (!cluster) return 0;

  const criteriaIndices = cluster.criterias;
  const availableCriteria = criteriaBreakdown.filter((item) =>
    criteriaIndices.includes(item.criteriaIndex)
  );

  if (availableCriteria.length === 0) return 0;

  // IA uses predefined weights instead of criteria calculation
  if (cluster.clasterSubject === "IA") {
    return getIAScore(industryKey);
  }

  // Calculate weighted average for CP and ER
  const weights = availableCriteria.map((item) => {
    const weight = industryWeights[item.criteriaIndex]?.[industryKey] || 0;
    return { ...item, weight };
  });

  const totalWeight = weights.reduce((sum, item) => sum + item.weight, 0);

  // Fallback to simple average if no weights available
  if (totalWeight === 0) {
    const avg =
      availableCriteria.reduce(
        (sum, item) => sum + getDisplayPercent(item),
        0
      ) / availableCriteria.length;
    return avg;
  }

  // Calculate weighted normalized score
  const normalizedScore = weights.reduce((sum, item) => {
    const normalizedWeight = item.weight / totalWeight;
    return sum + getDisplayPercent(item) * normalizedWeight;
  }, 0);

  return normalizedScore;
};

/**
 * Gets the predefined Industry Attractiveness score for a given industry
 */
export const getIAScore = (industryKey: string): number => {
  const key = industryKey as keyof typeof IAWeights;
  return IAWeights[key] || 55.04;
};

// ============================================================================
// STEP 2: Determine Strategic Levels
// ============================================================================

/**
 * Maps a numeric score to its corresponding strategic level
 */
export const getStrategicLevel = (score: number): StrategicLevel => {
  const level = StrategicRangeClassificationSRC.find(
    (item) => score >= item.min && score <= item.max
  );
  return level || { code: "N/A", title: "Unknown", min: 0, max: 0 };
};

// ============================================================================
// STEP 3: Generate Combinations and Find Matching Rules
// ============================================================================

/**
 * Expands transition values into possible states
 * T1 → L, M
 * T2 → M, H
 * Others → same value
 */
const expandTransitionValue = (code: string): string[] => {
  if (code === "T1") return ["L", "M"];
  if (code === "T2") return ["M", "H"];
  return [code];
};

/**
 * Generates all possible combinations of ER, CP, IA values
 * Handles transition expansion automatically
 */
const generateAllCombinations = (levels: {
  ER: StrategicLevel;
  CP: StrategicLevel;
  IA: StrategicLevel;
}): Array<{ ER: string; CP: string; IA: string }> => {
  const erOptions = expandTransitionValue(levels.ER.code);
  const cpOptions = expandTransitionValue(levels.CP.code);
  const iaOptions = expandTransitionValue(levels.IA.code);

  const combinations: Array<{ ER: string; CP: string; IA: string }> = [];

  for (const er of erOptions) {
    for (const cp of cpOptions) {
      for (const ia of iaOptions) {
        combinations.push({ ER: er, CP: cp, IA: ia });
      }
    }
  }

  return combinations;
};

/**
 * Finds the matching SDE rule for a given combination
 * Returns null if no rule matches
 */
const findSDERule = (levels: {
  ER: string;
  CP: string;
  IA: string;
}): RuleResult | null => {
  const rule = StrategicDecisionEngineSDE.find(
    (r) => r.IA === levels.IA && r.CP === levels.CP && r.ER === levels.ER
  );

  if (!rule) return null;

  return {
    RULE: rule.RULE,
    IA: rule.IA,
    CP: rule.CP,
    ER: rule.ER,
    description: rule.description,
    Confidence: rule.Confidence,
    StrategicDirection: rule.StrategicDirection,
  };
};

// ============================================================================
// STEP 4: Select Primary and Secondary Strategies
// ============================================================================

/**
 * Selects primary and secondary strategies based on frequency and priority
 * Primary: Most frequent strategy
 * Secondary: Second most frequent strategy (or next priority)
 */
const selectPrimarySecondary = (
  strategyList: string[]
): { primary: string; secondary?: string } => {
  if (strategyList.length === 0) {
    return { primary: "AnalyzeReview" };
  }

  if (strategyList.length === 1) {
    return { primary: strategyList[0] };
  }

  // Count frequency of each strategy
  const frequency: Record<string, number> = {};
  for (const s of strategyList) {
    frequency[s] = (frequency[s] || 0) + 1;
  }

  // Sort by: 1. Frequency (higher is better), 2. Priority (lower number = higher priority)
  const sorted = Object.entries(frequency).sort((a, b) => {
    // Primary: higher frequency
    if (b[1] !== a[1]) {
      return b[1] - a[1];
    }
    // Secondary: higher priority (lower number)
    const priorityA = STRATEGY_PRIORITY[a[0]] || 99;
    const priorityB = STRATEGY_PRIORITY[b[0]] || 99;
    return priorityA - priorityB;
  });

  const primary = sorted[0][0];
  const secondary = sorted.length > 1 ? sorted[1][0] : undefined;

  return { primary, secondary };
};

// ============================================================================
// STEP 5: Final Strategy and Boundary Evaluation
// ============================================================================

/**
 * Finds the final strategy based on transition items
 * Uses exact match first, then best match fallback
 */
const findFinalStrategy = (
  transitions: string[]
): FinalStrategyResult | null => {
  const sortedTransitions = [...transitions].sort();

  // Try exact match first
  let match = finalStrategy.find((item) => {
    const sortedItem = [...item.T].sort();
    return (
      sortedItem.length === sortedTransitions.length &&
      sortedItem.every((t, i) => t === sortedTransitions[i])
    );
  });

  // Fallback: best match (maximum intersection)
  if (!match) {
    let bestMatch: FinalStrategyResult | null = null;
    let bestScore = 0;

    finalStrategy.forEach((item) => {
      const intersection = item.T.filter((t) => transitions.includes(t));
      const score =
        intersection.length / Math.max(item.T.length, transitions.length);
      if (score > bestScore) {
        bestScore = score;
        bestMatch = item;
      }
    });

    match = bestMatch;
  }

  return match;
};

/**
 * Evaluates the strategic boundary based on number of transitions
 */
const evaluateBoundary = (transitionCount: number) => {
  const evaluation = StrategicBoundaryEvaluationSBE.find(
    (item) => item.transitionCount === transitionCount
  );
  return (
    evaluation || {
      transitionCount: 0,
      ruleCount: 1,
      StrategicStability: StrategyStability.VeryHigh,
      DecisionStatus: "Stable",
    }
  );
};

// ============================================================================
// MAIN FUNCTION: Complete Strategy Calculation
// ============================================================================

/**
 * Calculates the complete strategic analysis result
 *
 * Flow:
 * 1. Calculate IA, CP, ER scores
 * 2. Determine strategic levels (L, T1, M, T2, H)
 * 3. Generate all possible combinations and find matching rules
 * 4. Select primary and secondary strategies
 * 5. Determine final strategy and boundary evaluation
 */
export const calculateCompleteStrategy = (
  criteriaBreakdown: CriteriaBreakdown[],
  industryWeights: { [key: number]: { [key: string]: number } },
  industryKey: string
): StrategyResult => {
  // ──────────────────────────────────────────────────────────────
  // Step 1: Calculate IA, CP, ER Scores
  // ──────────────────────────────────────────────────────────────
  const erScore = calculateClusterScore(
    criteriaBreakdown,
    "EnvironmentalSustainabilityAndResilience",
    industryWeights,
    industryKey
  );

  const cpScore = calculateClusterScore(
    criteriaBreakdown,
    "CompetitivePosition",
    industryWeights,
    industryKey
  );

  const iaScore = getIAScore(industryKey);

  const scores = { ER: erScore, CP: cpScore, IA: iaScore };

  // ──────────────────────────────────────────────────────────────
  // Step 2: Determine Strategic Levels
  // ──────────────────────────────────────────────────────────────
  const levels = {
    ER: getStrategicLevel(erScore),
    CP: getStrategicLevel(cpScore),
    IA: getStrategicLevel(iaScore),
  };

  // Identify transitions
  const transitions: string[] = [];
  if (levels.ER.transition) transitions.push("ER");
  if (levels.CP.transition) transitions.push("CP");
  if (levels.IA.transition) transitions.push("IA");

  const isTransitional = transitions.length > 0;

  // ──────────────────────────────────────────────────────────────
  // Step 3: Generate All Combinations & Find Matching Rules
  // ──────────────────────────────────────────────────────────────
  const allCombinations = generateAllCombinations(levels);

  const allRules: RuleResult[] = [];
  const strategyList: string[] = [];

  for (const combo of allCombinations) {
    const rule = findSDERule(combo);
    if (rule) {
      allRules.push(rule);
      strategyList.push(rule.StrategicDirection);
    }
  }

  // ──────────────────────────────────────────────────────────────
  // Step 4: Select Primary & Secondary Strategies
  // ──────────────────────────────────────────────────────────────
  const { primary, secondary } = selectPrimarySecondary(strategyList);

  // Count strategy occurrences
  const strategyCounts: Record<string, number> = {};
  for (const s of strategyList) {
    strategyCounts[s] = (strategyCounts[s] || 0) + 1;
  }

  // ──────────────────────────────────────────────────────────────
  // Step 5: Final Strategy & Boundary Evaluation
  // ──────────────────────────────────────────────────────────────
  const finalStrategyResult = findFinalStrategy(transitions);
  const boundaryEvaluation = evaluateBoundary(transitions.length);

  // ──────────────────────────────────────────────────────────────
  // Return Complete Result
  // ──────────────────────────────────────────────────────────────
  return {
    scores,
    levels,
    allRules,
    allCombinations,
    primaryStrategy: primary,
    secondaryStrategy: secondary,
    isTransitional,
    transitions,
    strategyCounts,
    finalStrategy: finalStrategyResult,
    boundaryEvaluation,
  };
};
